/**
 * Campaign style constants and utilities.
 *
 * Per epic 019 (Q66 reversal): four first-class campaign styles.
 * - Co-op (delivery surface, no hosted page)
 * - Custom Opt-in (delivery surface, has hosted page)
 * - Paid Social (capture journey)
 * - CTO Email (capture journey, parameter capture only)
 */

/**
 * The four campaign styles (user-facing labels).
 */
export const CAMPAIGN_STYLES = ['Co-op', 'Custom Opt-in', 'Paid Social', 'CTO Email'] as const;

export type CampaignStyle = (typeof CAMPAIGN_STYLES)[number];

/**
 * Map user-facing style labels to backend campaignStyle enum values.
 */
export const STYLE_LABEL_TO_ENUM: Record<
  CampaignStyle,
  'co_op' | 'custom_optin' | 'paid_social' | 'cto_email'
> = {
  'Co-op': 'co_op',
  'Custom Opt-in': 'custom_optin',
  'Paid Social': 'paid_social',
  'CTO Email': 'cto_email',
} as const;

/**
 * Map backend campaignStyle enum values to user-facing labels.
 */
export const STYLE_ENUM_TO_LABEL: Record<
  'co_op' | 'custom_optin' | 'paid_social' | 'cto_email',
  CampaignStyle
> = {
  co_op: 'Co-op',
  custom_optin: 'Custom Opt-in',
  paid_social: 'Paid Social',
  cto_email: 'CTO Email',
} as const;

/**
 * Convert a campaign style to its flowKind value.
 * Co-op maps to 'coreg', all others map to 'hlp'.
 */
export function styleToFlowKind(style: CampaignStyle): 'coreg' | 'hlp' {
  if (style === 'Co-op') return 'coreg';
  return 'hlp'; // Custom Opt-in, Paid Social, CTO Email all map to hlp
}

/**
 * Convert backend campaignStyle enum to its flowKind value.
 */
export function campaignStyleEnumToFlowKind(
  style: 'co_op' | 'custom_optin' | 'paid_social' | 'cto_email'
): 'coreg' | 'hlp' {
  if (style === 'co_op') return 'coreg';
  return 'hlp'; // custom_optin, paid_social, cto_email all map to hlp
}

/**
 * Convert backend campaignStyle enum to user-facing label.
 */
export function campaignStyleEnumToLabel(
  style: 'co_op' | 'custom_optin' | 'paid_social' | 'cto_email'
): CampaignStyle {
  return STYLE_ENUM_TO_LABEL[style];
}

/**
 * Check if a style is a journey style (shows journey sections).
 * Journey styles: Paid Social, CTO Email.
 */
export function isJourneyStyle(style: CampaignStyle): boolean {
  return style === 'Paid Social' || style === 'CTO Email';
}

/**
 * Check if a style is Co-op.
 */
export function isCoopStyle(style: CampaignStyle): boolean {
  return style === 'Co-op';
}

/**
 * Check if a style is Custom Opt-in.
 */
export function isCustomOptinStyle(style: CampaignStyle): boolean {
  return style === 'Custom Opt-in';
}

/**
 * Check if a style is Paid Social.
 */
export function isPaidSocialStyle(style: CampaignStyle): boolean {
  return style === 'Paid Social';
}

/**
 * Check if a style is CTO Email.
 */
export function isCtoEmailStyle(style: CampaignStyle): boolean {
  return style === 'CTO Email';
}

/**
 * Get the tone for Tag/StatusPill from campaign style.
 */
export function styleToTone(
  style: CampaignStyle
): 'neutral' | 'accent' | 'amber' | 'violet' | 'sky' | 'rose' {
  const toneMap: Record<CampaignStyle, 'neutral' | 'accent' | 'amber' | 'violet' | 'sky' | 'rose'> =
    {
      'Co-op': 'amber',
      'Custom Opt-in': 'violet',
      'Paid Social': 'sky',
      'CTO Email': 'rose',
    };
  return toneMap[style];
}

const DEFAULT_OFFER_PAGE_BASE = 'https://offers.tradingtips.com';

/**
 * HF-123: trim surrounding whitespace and strip every trailing "/" so the
 * `/<slug>` join never yields "//". Empty, whitespace-only, "/" or undefined
 * falls back to the production URL.
 */
export function normalizeOfferPageBase(raw: string | undefined): string {
  const stripped = (raw ?? '').trim().replace(/\/+$/, '');
  return stripped || DEFAULT_OFFER_PAGE_BASE;
}

/**
 * Offer page base URL for copy-full-URL actions.
 * Falls back to production URL if env var not set (for local dev).
 * HF-123: a trailing slash in the env value is stripped (see normalizeOfferPageBase).
 */
export const OFFER_PAGE_BASE = normalizeOfferPageBase(import.meta.env.VITE_OFFER_PAGE_BASE_URL);

/**
 * Build the full page URL from a slug: the canonical public path is /<slug>
 * (epic 057 middleware rule b rewrites it internally to the landing-client
 * route /welcome/:slug, ADR-005 — the browser URL stays /<slug>).
 * Returns null if no slug (Co-op campaigns have no hosted page).
 */
export function pageUrl(slug: string | null | undefined): string | null {
  if (!slug) return null;
  return `${OFFER_PAGE_BASE}/${slug}`;
}

/**
 * Limit types - narrowed to 3 actually used per admin-requirements.md.
 */
export const LIMIT_TYPES = [
  'Total Leads Limit',
  'Unique Leads Limit',
  'Total Clicks Limit',
] as const;

export type LimitType = (typeof LIMIT_TYPES)[number];

/**
 * Status values for campaigns.
 */
export const STATUSES = ['Active', 'Pending', 'Completed', 'Test', 'Scheduled', 'Paused'] as const;

export type CampaignStatusValue = (typeof STATUSES)[number];

/**
 * Thank-you message default (production copy).
 * From admin-requirements.md §2.2 / §3.4 with corrected </h1>.
 */
export const THANK_YOU_DEFAULT =
  "<h3>Thank you! Your free report is on its way. But DON'T LEAVE JUST YET!</h3>\n" +
  '<h1>You have been selected to receive one of these offers below.</h1>';

/**
 * CRM placeholders by syntax type.
 */
export const PLACEHOLDER_SYNTAXES = [
  { key: 'percent', label: '%email% (percent)', sample: '%email%' },
  { key: 'bracket', label: '[[EMAIL-ADDRESS-HERE]] (bracket)', sample: '[[EMAIL-ADDRESS-HERE]]' },
] as const;

export type PlaceholderSyntax = (typeof PLACEHOLDER_SYNTAXES)[number]['key'];

export const CRM_PLACEHOLDERS: Record<string, string[]> = {
  percent: [
    '%email%',
    '%name%',
    '%fname%',
    '%lname%',
    '%site_id%',
    '%sub_id%',
    '%ip_address%',
    '%md5%',
  ],
  bracket: [
    '[[EMAIL-ADDRESS-HERE]]',
    '[[FIRST-NAME]]',
    '[[LAST-NAME]]',
    '[[SITE-ID]]',
    '[[SUB-ID]]',
    '[[IP-ADDRESS]]',
  ],
};

/**
 * Eligibility step options (Q57).
 */
export const ELIGIBILITY_STEPS = [
  {
    key: 'cloudflare',
    label: 'Cloudflare Turnstile',
    desc: 'Human verification — per-campaign configurable, only on certain inbound sources',
  },
  {
    key: 'emailoversight',
    label: 'EmailOversight',
    desc: 'Email address validation via the EmailOversight API',
  },
  {
    key: 'dedup',
    label: 'Internal Duplicate Check',
    desc: 'A duplicate is not automatically ineligible — configured behavior',
  },
] as const;

/**
 * Follow-on component types (journey primitives).
 */
export const FOLLOW_ON_TYPES = [
  'Presented Offer Sequence',
  'Background Distribution',
  'Render Page',
  'Redirect',
  'Finish Journey',
] as const;

export type FollowOnType = (typeof FOLLOW_ON_TYPES)[number];
